import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AlertRuleInput,
  CreateTenantInput,
  InviteUserInput,
  Role,
  SaveVehiclePlateInput,
  UpdateDeviceInput,
  UpdateTenantInput,
  UpdateUnifiIntegrationInput,
} from '@iot-dashboard/shared';
import { useApi } from './ApiProvider';
import type { DeviceFilters, PlateFilters } from './client';

export const keys = {
  me: ['me'] as const,
  msp: ['msp-overview'] as const,
  tenants: ['tenants'] as const,
  tenant: (id: string) => ['tenant', id] as const,
  overview: (id: string) => ['tenant', id, 'overview'] as const,
  sites: (id: string) => ['tenant', id, 'sites'] as const,
  devices: (id: string, f?: DeviceFilters) => ['tenant', id, 'devices', f ?? {}] as const,
  device: (id: string, d: string) => ['tenant', id, 'device', d] as const,
  readings: (id: string, d: string, from: string, to: string) =>
    ['tenant', id, 'device', d, 'readings', from, to] as const,
  alerts: (id: string, status: string) => ['tenant', id, 'alerts', status] as const,
  rules: (id: string) => ['tenant', id, 'rules'] as const,
  events: (id: string, limit?: number, type?: string) => ['tenant', id, 'events', limit, type] as const,
  plates: (id: string, f?: PlateFilters) => ['tenant', id, 'plates', f ?? {}] as const,
  // Outside the tenant's key so refreshing tenant data never refetches pictures.
  plateThumbnail: (id: string, detectionId: string) => ['plate-thumbnail', id, detectionId] as const,
  users: (id: string) => ['tenant', id, 'users'] as const,
  integration: (id: string) => ['tenant', id, 'integration'] as const,
};

const LIVE = { refetchInterval: 60_000, staleTime: 15_000 };

export const useMe = () => {
  const api = useApi();
  return useQuery({ queryKey: keys.me, queryFn: () => api.me(), staleTime: 60_000 });
};

export const useMspOverview = () => {
  const api = useApi();
  return useQuery({ queryKey: keys.msp, queryFn: () => api.mspOverview(), ...LIVE });
};

export const useTenants = () => {
  const api = useApi();
  return useQuery({ queryKey: keys.tenants, queryFn: () => api.listTenants() });
};

export const useTenant = (id: string) => {
  const api = useApi();
  return useQuery({ queryKey: keys.tenant(id), queryFn: () => api.getTenant(id) });
};

export const useTenantOverview = (id: string) => {
  const api = useApi();
  return useQuery({ queryKey: keys.overview(id), queryFn: () => api.tenantOverview(id), ...LIVE });
};

export const useSites = (id: string) => {
  const api = useApi();
  return useQuery({ queryKey: keys.sites(id), queryFn: () => api.listSites(id), enabled: id.length > 0, ...LIVE });
};

export const useDevices = (id: string, filters?: DeviceFilters) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.devices(id, filters),
    queryFn: () => api.listDevices(id, filters),
    placeholderData: (prev) => prev,
    ...LIVE,
  });
};

export const useDevice = (id: string, deviceId: string) => {
  const api = useApi();
  return useQuery({ queryKey: keys.device(id, deviceId), queryFn: () => api.getDevice(id, deviceId), ...LIVE });
};

export const useReadings = (id: string, deviceId: string, from: string, to: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.readings(id, deviceId, from, to),
    queryFn: () => api.listReadings(id, deviceId, from, to),
    placeholderData: (prev) => prev,
    staleTime: 60_000,
  });
};

export const useAlerts = (id: string, status: 'open' | 'resolved' | 'all') => {
  const api = useApi();
  return useQuery({ queryKey: keys.alerts(id, status), queryFn: () => api.listAlerts(id, status), ...LIVE });
};

export const useRules = (id: string) => {
  const api = useApi();
  return useQuery({ queryKey: keys.rules(id), queryFn: () => api.listRules(id) });
};

export const useEvents = (id: string, limit = 100, type?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.events(id, limit, type),
    queryFn: () => api.listEvents(id, limit, type),
    placeholderData: (prev) => prev,
    ...LIVE,
  });
};

export const usePlates = (id: string, filters?: PlateFilters) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.plates(id, filters),
    queryFn: () => api.listPlates(id, filters),
    placeholderData: (prev) => prev,
    ...LIVE,
  });
};

/**
 * The picture sent with a plate read. It never changes, so one fetched for
 * the table is reused by the larger view without asking the API again.
 */
export const usePlateThumbnail = (id: string, detectionId: string, enabled: boolean) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.plateThumbnail(id, detectionId),
    queryFn: () => api.getPlateThumbnail(id, detectionId),
    enabled,
    staleTime: Infinity,
    gcTime: 10 * 60_000,
  });
};

export const useUsers = (id: string, enabled = true) => {
  const api = useApi();
  return useQuery({ queryKey: keys.users(id), queryFn: () => api.listUsers(id), enabled });
};

export const useIntegration = (id: string) => {
  const api = useApi();
  return useQuery({ queryKey: keys.integration(id), queryFn: () => api.getIntegration(id), ...LIVE });
};

// ------------------------------------------------------------------ mutations

function useInvalidate() {
  const qc = useQueryClient();
  return (...queryKeys: readonly (readonly unknown[])[]) =>
    Promise.all(queryKeys.map((queryKey) => qc.invalidateQueries({ queryKey })));
}

export const useCreateTenant = () => {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: CreateTenantInput) => api.createTenant(input),
    onSuccess: () => invalidate(keys.tenants, keys.msp, keys.me),
  });
};

export const useUpdateTenant = (id: string) => {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (patch: UpdateTenantInput) => api.updateTenant(id, patch),
    onSuccess: () => invalidate(keys.tenant(id), keys.overview(id), keys.tenants, keys.msp, keys.me),
  });
};

export const useSuspendTenant = (id: string) => {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => api.suspendTenant(id),
    onSuccess: () => invalidate(keys.tenant(id), keys.tenants, keys.msp, keys.me),
  });
};

export const useDeleteTenant = (id: string) => {
  const api = useApi();
  const qc = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (confirmSlug: string) => api.deleteTenant(id, confirmSlug),
    onSuccess: () => {
      // Drop the portal's list rather than mark it stale, or the portal shows the
      // deleted tenant until its refetch lands. The tenant's own queries are left
      // alone: refetching them would only 404.
      qc.removeQueries({ queryKey: keys.msp });
      return invalidate(keys.tenants, keys.me);
    },
  });
};

export const useUpdateDevice = (id: string, deviceId: string) => {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (patch: UpdateDeviceInput) => api.updateDevice(id, deviceId, patch),
    onSuccess: () => invalidate(keys.device(id, deviceId), ['tenant', id, 'devices']),
  });
};

/**
 * Saves or forgets the plate of a vehicle Protect sends by name. Every list
 * of reads shows saved plates, so all of them are refreshed.
 */
export const useSaveVehiclePlate = (id: string) => {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ vehicleKey, ...input }: SaveVehiclePlateInput & { vehicleKey: string }) =>
      api.saveVehiclePlate(id, vehicleKey, input),
    onSuccess: () => invalidate(['tenant', id, 'plates']),
  });
};

export const useDeleteVehiclePlate = (id: string) => {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (vehicleKey: string) => api.deleteVehiclePlate(id, vehicleKey),
    onSuccess: () => invalidate(['tenant', id, 'plates']),
  });
};

export const useResolveAlert = (id: string) => {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (alertId: string) => api.resolveAlert(id, alertId),
    onSuccess: () => invalidate(['tenant', id]),
  });
};

export const useSaveRule = (id: string) => {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ ruleId, input }: { ruleId?: string; input: AlertRuleInput }) =>
      ruleId ? api.updateRule(id, ruleId, input) : api.createRule(id, input),
    onSuccess: () => invalidate(keys.rules(id)),
  });
};

export const useDeleteRule = (id: string) => {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (ruleId: string) => api.deleteRule(id, ruleId),
    onSuccess: () => invalidate(keys.rules(id)),
  });
};

export const useInviteUser = (id: string) => {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: InviteUserInput) => api.inviteUser(id, input),
    onSuccess: () => invalidate(keys.users(id), keys.events(id)),
  });
};

export const useUpdateUserRole = (id: string) => {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ username, role }: { username: string; role: Exclude<Role, 'msp-admin'> }) =>
      api.updateUserRole(id, username, role),
    onSuccess: () => invalidate(keys.users(id)),
  });
};

export const useResendInvite = (id: string) => {
  const api = useApi();
  return useMutation({ mutationFn: (username: string) => api.resendInvite(id, username) });
};

export const useRemoveUser = (id: string) => {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (username: string) => api.removeUser(id, username),
    onSuccess: () => invalidate(keys.users(id)),
  });
};

export const useResetUserMfa = (id: string) => {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (username: string) => api.resetUserMfa(id, username),
    onSuccess: () => invalidate(keys.users(id), keys.events(id)),
  });
};

export const useUpdateIntegration = (id: string) => {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: UpdateUnifiIntegrationInput) => api.updateIntegration(id, input),
    onSuccess: () => invalidate(keys.integration(id), keys.overview(id)),
  });
};

export const useDeleteIntegration = (id: string) => {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => api.deleteIntegration(id),
    onSuccess: () => invalidate(keys.integration(id), keys.overview(id)),
  });
};

export const useTestIntegration = (id: string) => {
  const api = useApi();
  return useMutation({ mutationFn: (apiKey?: string) => api.testIntegration(id, apiKey) });
};

export const useSyncNow = (id: string) => {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => api.syncNow(id),
    onSuccess: () => setTimeout(() => invalidate(['tenant', id]), 3000),
  });
};
